// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import { faInfoCircle } from "@fullscript/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FloatingLabelInput } from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import type {
  LegalInformationErrors,
  LegalInformationData,
} from "@shared/types/fields/legalInformationTypes";

import { IconWrapper } from "../../IconWrapper/IconWrapper";

import { SSNTooltip } from "./SSNTooltip";

type PersonalInfoFieldsType = Pick<
  LegalInformationData,
  "firstName" | "lastName" | "dateOfBirth" | "ssn"
>;

type PersonalInfoFieldsErrors = Pick<
  LegalInformationErrors["fields"],
  "firstName" | "lastName" | "dateOfBirth" | "ssn"
>;

interface Props {
  values: PersonalInfoFieldsType;
  errors: PersonalInfoFieldsErrors;
  onChange: (values: PersonalInfoFieldsType) => void;
  isFullSsnRequired: boolean;
  labelOverrides?: Partial<PersonalInfoFieldsType>;
  readonly?: boolean;
  hideNameFields?: boolean;
  setIsSSNValid?: (boolean) => void;
}

const DATE_MASK = "####-##-##";
const DATE_PLACEHOLDER = "YYYY-MM-DD";
const DATE_MASK_MAP = ["Y", "Y", "Y", "Y", "M", "M", "D", "D"];

const PersonalInfoFields = ({
  values,
  errors,
  onChange,
  isFullSsnRequired,
  labelOverrides,
  readonly,
  hideNameFields,
  setIsSSNValid,
}: Props) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();

  const [ssn, setSSN] = useState("");
  const [isSSNDirty, setIsSSNDirty] = useState(false);

  const isOnlyPositiveIntegers = (text: string): boolean => {
    if (text === "") return true;
    const lastChar = text?.slice(-1);
    const allowCharacters = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

    return allowCharacters.indexOf(lastChar) > -1;
  };

  const isSSNValid = (ssnValue: any) => {
    if (
      isUS &&
      ((isFullSsnRequired && ssnValue.length < 9) || (!isFullSsnRequired && ssnValue.length < 4))
    ) {
      setIsSSNValid(false);
    } else if (!isUS && ssnValue.length < 9) {
      setIsSSNValid(false);
    } else {
      setIsSSNValid(true);
    }
  };

  const handleSSNOnBlur = e => {
    if (e.target.value) {
      setIsSSNDirty(true);
      setSSN(e.target.value);
      isSSNValid(e.target.value);
    }
  };

  const getSSNErrors = () => {
    if (!isSSNDirty) return null;
    if (isUS) {
      if (isFullSsnRequired && ssn.length < 9) {
        return t(l.sharedOnboarding.fullSSNIsRequired);
      } else if (!isFullSsnRequired && ssn.length < 4) {
        return t(l.sharedOnboarding.last4DigitsOfSSNRequired);
      }
    } else if (!isUS && ssn.length < 9) {
      return t(l.sharedOnboarding.fullSINIsRequired);
    }
  };

  const handleSSNOnChange = e => {
    if (!isOnlyPositiveIntegers(e.target.value)) return;

    setSSN(e.target.value);
    onChange({
      ...values,
      [e.target.id]: e.target.value,
    });
  };

  const handleOnChange = e => {
    onChange({
      ...values,
      [e.target.id]: e.target.value,
    });
  };

  const getSSNLabel = () => {
    if (labelOverrides?.ssn) return labelOverrides.ssn;

    if (isUS) {
      if (isFullSsnRequired) {
        return t(l.sharedOnboarding.SSN);
      } else {
        return t(l.sharedOnboarding.last4DigitsOfSSN);
      }
    }

    return t(l.sharedOnboarding.sinNumber);
  };

  return (
    <>
      {!hideNameFields && (
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="firstName"
            type="text"
            name={t(l.sharedOnboarding.legalFirstName)}
            label={labelOverrides?.firstName ?? t(l.sharedOnboarding.legalFirstName)}
            value={values?.firstName ?? ""}
            handleChange={handleOnChange}
            required
            errors={errors?.firstName}
            disabled={readonly}
            data-e2e="first-name"
          />
        </Columns.Column>
      )}

      {!hideNameFields && (
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="lastName"
            type="text"
            name={t(l.sharedOnboarding.legalLastName)}
            label={labelOverrides?.lastName ?? t(l.sharedOnboarding.legalLastName)}
            value={values?.lastName ?? ""}
            handleChange={handleOnChange}
            required
            errors={errors?.lastName}
            disabled={readonly}
            data-e2e="last-name"
          />
        </Columns.Column>
      )}

      <Columns.Column columnWidth={6}>
        <FloatingLabelInput
          mask={DATE_MASK}
          placeholder={DATE_PLACEHOLDER}
          maskMap={DATE_MASK_MAP}
          type="text"
          id="dateOfBirth"
          name={t(l.sharedOnboarding.dateOfBirth)}
          label={labelOverrides?.dateOfBirth ?? t(l.sharedOnboarding.dateOfBirth)}
          value={values?.dateOfBirth ?? ""}
          handleChange={handleOnChange}
          required
          errors={errors?.dateOfBirth}
          disabled={readonly}
          data-e2e="date-of-birth"
        />
      </Columns.Column>

      <Columns.Column columnWidth={6}>
        <IconWrapper icon={faInfoCircle} tooltipMessage={<SSNTooltip />}>
          <FloatingLabelInput
            id="ssn"
            type="text"
            name={t(l.sharedOnboarding.sinNumber)}
            label={getSSNLabel()}
            value={values?.ssn ?? ""}
            handleChange={handleSSNOnChange}
            required
            maxLength={isUS && !isFullSsnRequired ? 4 : 9}
            errors={errors?.ssn || [getSSNErrors()]}
            disabled={readonly}
            onBlurCapture={handleSSNOnBlur}
            data-e2e={e2eAttributes.paymentSettings.ssn}
          />
        </IconWrapper>
      </Columns.Column>
    </>
  );
};

export {
  PersonalInfoFields,
  type Props as PersonalInfoFieldsProps,
  type PersonalInfoFieldsType,
  type PersonalInfoFieldsErrors,
};
